/**
 * Pure derivation helpers for the Humaans-style time-away view: list splitting,
 * year grouping, per-policy usage rows and the Details-card facts. No React —
 * the cards stay dumb and everything here is unit-testable with fixed dates.
 */
import { t } from '../../../../../../../common/i18n.js'
import {
    accrualWindowStart,
    computeAccruedBalance,
} from '../../../../../../../common/leave-accrual.js'
import { countLeaveDays } from '../../../../../../../common/leave-days.js'
import {
    approvedDaysByPolicy,
    policyUsedDays,
    roundDays,
} from '../../../../../../../common/leave-usage.js'
import type { Language } from '../../../../../../../common/types/i18n.js'
import { getLeaveBadgeEmoji } from '../../../../../../../common/utils/leave-badge-emoji.js'
import type { LeaveRequest, ProfileLeaveBalance } from '../../employee-profile-types.js'

const SLUG_VACATION = 'pohipuhkus'
const SLUG_TOOVABA = 'toovaba-nadal'

export interface YearSplitPart {
    year: number
    days: number
}

/**
 * A history-row request annotated for its year group. `yearDays`/`yearSplit`
 * are set only on year-spanning requests: the row shows the days attributable
 * to the group's year (Humaans-style) and the split feeds the ⓘ tooltip.
 */
export type YearListedRequest = LeaveRequest & {
    yearDays?: number
    yearSplit?: YearSplitPart[]
}

export interface YearGroup {
    year: number
    requests: YearListedRequest[]
}

export interface YearSummaryRow {
    leaveTypeName: string
    emoji: string
    days: number
    count: number
}

export interface DaysTakenRow {
    policyId: number
    emoji: string
    leaveTypeName: string
    used: number
    totalEntitlement: number
    carryover: number
    label: string
}

export interface CarryoverRow {
    policyId: number
    emoji: string
    leaveTypeName: string
    amount: number
    date: string
}

export interface TimeAwayDetails {
    policyName: string | null
    approvalMode: 'manager' | 'automatic' | null
    toovabaAssigned: boolean
}

// Canonical selector is the seed/Humaans slug; the badge fallback covers
// custom types that reuse the vacation badge without the canonical slug.
const findVacation = (balances: ProfileLeaveBalance[]): ProfileLeaveBalance | undefined =>
    balances.find((b) => b.leaveTypeSlug === SLUG_VACATION) ??
    balances.find((b) => b.badgeType === 'vacation')

const isVacation = (b: ProfileLeaveBalance): boolean =>
    b.leaveTypeSlug === SLUG_VACATION || b.badgeType === 'vacation'

/** APPROVED + PENDING requests still ahead (incl. ongoing), soonest first. */
export const splitUpcoming = (requests: LeaveRequest[], today: string): LeaveRequest[] =>
    requests
        .filter((r) => (r.status === 'APPROVED' || r.status === 'PENDING') && r.end_date >= today)
        .sort((a, b) => a.start_date.localeCompare(b.start_date))

/**
 * Concluded APPROVED requests grouped by year, newest first on both levels.
 * A year-spanning request appears in EVERY year it touches, its row showing
 * the days attributable to that year — otherwise the new-year days would be
 * invisible in the current-year card while still counting as taken. Later
 * years are counted holiday-aware from the clipped range; the first year takes
 * the remainder, so the parts always sum to the request's own business days
 * even when older years' holidays aren't loaded.
 */
export const groupApprovedByYear = (
    requests: LeaveRequest[],
    today: string,
    holidayDates: string[] = [],
): YearGroup[] => {
    const byYear = new Map<number, YearListedRequest[]>()
    const push = (year: number, entry: YearListedRequest) => {
        const group = byYear.get(year)
        if (group) group.push(entry)
        else byYear.set(year, [entry])
    }

    for (const request of requests) {
        if (request.status !== 'APPROVED' || request.end_date >= today) continue
        const startYear = Number(request.start_date.slice(0, 4))
        const endYear = Number(request.end_date.slice(0, 4))
        if (endYear === startYear) {
            push(startYear, request)
            continue
        }

        const split: YearSplitPart[] = []
        let laterDays = 0
        for (let year = startYear + 1; year <= endYear; year++) {
            const clipEnd = year === endYear ? request.end_date : `${year}-12-31`
            const days = countLeaveDays(`${year}-01-01`, clipEnd, holidayDates)
            split.push({ year, days })
            laterDays += days
        }
        split.unshift({ year: startYear, days: Math.max(0, request.business_days - laterDays) })

        for (const part of split) {
            // A part can be 0 days (its slice was all holidays) — no row for it,
            // except in the home year so the request is always listed somewhere.
            if (part.days > 0 || part.year === startYear) {
                push(part.year, { ...request, yearDays: part.days, yearSplit: split })
            }
        }
    }

    return [...byYear.entries()]
        .sort(([a], [b]) => b - a)
        .map(([year, group]) => ({
            year,
            requests: [...group].sort((a, b) => b.start_date.localeCompare(a.start_date)),
        }))
}

/**
 * Per-type totals for a year card's default summary view (TASK-232) — the
 * Humaans-style rollup shown before "Näita detailselt". Sums the days
 * attributable to the group's year (`yearDays` on year-spanning rows), so the
 * summary always matches the sum of its own year's detailed rows.
 */
export const summarizeYearRequests = (requests: YearListedRequest[]): YearSummaryRow[] => {
    const byType = new Map<string, YearSummaryRow>()
    for (const request of requests) {
        const days = request.yearDays ?? request.business_days
        const row = byType.get(request.leaveTypeName)
        if (row) {
            row.days = roundDays(row.days + days)
            row.count += 1
        } else {
            byType.set(request.leaveTypeName, {
                leaveTypeName: request.leaveTypeName,
                emoji: getLeaveBadgeEmoji(request.badgeType),
                days: roundDays(days),
                count: 1,
            })
        }
    }
    return [...byType.values()].sort(
        (a, b) => b.days - a.days || a.leaveTypeName.localeCompare(b.leaveTypeName),
    )
}

/**
 * Per-policy usage rows; zero-information rows are collapsed behind "N more".
 * Non-accruing policies (siseüritus, haigusleht, …) never get ledger
 * deductions, so their usage is derived from the year's approved requests.
 */
export const buildDaysTakenRows = (
    balances: ProfileLeaveBalance[],
    lang: Language,
    requests: LeaveRequest[] = [],
    year?: number,
): { visible: DaysTakenRow[]; collapsed: DaysTakenRow[] } => {
    const view = t.portal.timeOff.view
    const requestDays = approvedDaysByPolicy(requests, year)
    const rows = [...balances]
        .sort((a, b) => {
            if (isVacation(a) !== isVacation(b)) return isVacation(a) ? -1 : 1
            return a.leaveTypeName.localeCompare(b.leaveTypeName)
        })
        .map((b) => {
            const used = policyUsedDays(b, requestDays)
            const totalEntitlement = roundDays(b.totalEntitlement)
            return {
                policyId: b.policyId,
                emoji: getLeaveBadgeEmoji(b.badgeType),
                leaveTypeName: b.leaveTypeName,
                used,
                totalEntitlement,
                carryover: roundDays(b.carryover),
                label:
                    b.leaveTypeSlug === SLUG_TOOVABA
                        ? used > 0
                            ? view.toovabaUsed(lang)
                            : view.toovabaUnused(lang)
                        : totalEntitlement > 0
                          ? view.daysTakenOf(used, totalEntitlement)(lang)
                          : view.dayCount(used)(lang),
            }
        })
    return {
        visible: rows.filter((r) => r.used > 0 || r.totalEntitlement > 0),
        collapsed: rows.filter((r) => r.used === 0 && r.totalEntitlement === 0),
    }
}

/** Synthetic highlighted "+X days carried over" rows, pinned to 1 Jan of the year. */
export const buildCarryoverRows = (balances: ProfileLeaveBalance[], year: number): CarryoverRow[] =>
    balances
        .filter((b) => b.carryover > 0)
        .map((b) => ({
            policyId: b.policyId,
            emoji: getLeaveBadgeEmoji(b.badgeType),
            leaveTypeName: b.leaveTypeName,
            amount: roundDays(b.carryover),
            date: `${year}-01-01`,
        }))

export interface VacationStatBreakdown {
    emoji: string
    leaveTypeName: string
    policyName: string
    // Allowance side: base norm + carried-over days + admin corrections
    // (TASK-567, full-count facts as of their date) = total allowance.
    allowance: number
    carryover: number
    adjustments: number
    totalAllowance: number
    // Usage side: ledger deductions split into concluded vs booked-ahead
    // (drives the year-end "remaining" tooltip — bucket-consistent).
    usedPast: number
    upcomingApproved: number
    // Hero number 1: remaining through year-end = the server's `available`,
    // which already reserves PENDING days — showing them as free would invite
    // double-booking while a request waits for approval.
    remaining: number
    pending: number
    unplanned: number
    // Hero number 2: balance accrued as of today (Humaans-style) = current-year
    // entitlement prorated to today + carryover − days actually TAKEN so far.
    // `takenThisYear` is date-accurate (from concluded requests), unlike the
    // ledger-derived `usedPast`, so it matches Humaans even when the opening
    // lump deduction and the request dates disagree.
    accruedToDate: number
    totalAccrued: number
    takenThisYear: number
    balanceAsOfToday: number
    /** First day of the accrual window — feeds the "Accrued since …" tooltip label */
    accruedSince: string
}

/**
 * Põhipuhkus hero numbers plus the arithmetic behind them, for the stat
 * tooltips. The ledger deducts at approval time, so `used` is split into
 * concluded vs booked-ahead via the request list (matched by policy name).
 *
 * `remaining` is the server's bucket-precise `available` (pending already
 * netted out) instead of re-deriving total − used — once days can expire
 * (TLS §68), that identity drifts around year boundaries.
 *
 * `balanceAsOfToday` is the Humaans-style real-time view: although the portal
 * grants the year upfront, days are *earned* evenly over the year, so this
 * shows the current-year norm prorated to today, plus carryover (fully earned
 * last year), minus days actually taken — booked-ahead leave hasn't been taken
 * yet and is NOT subtracted. Rounded to 0.1 like Humaans' headline; the
 * accrued components keep 2 decimals to make the tooltip arithmetic readable.
 */
export const vacationStatBreakdown = (
    balances: ProfileLeaveBalance[],
    requests: LeaveRequest[],
    today: string,
): VacationStatBreakdown | null => {
    const vacation = findVacation(balances)
    if (!vacation) return null
    const upcomingApproved = roundDays(
        requests
            .filter(
                (r) =>
                    r.policyName === vacation.policyName &&
                    r.status === 'APPROVED' &&
                    r.end_date >= today,
            )
            .reduce((sum, r) => sum + r.business_days, 0),
    )
    // The norm/carryover/adjustments split is the server's bucket summary (the
    // same one the dashboard headline uses) — not lifetime entitlement minus
    // carryover, which would also count expired buckets and prorate admin
    // corrections into the norm.
    const allowance = roundDays(vacation.allowance)
    const carryover = roundDays(vacation.carryover)
    const adjustments = roundDays(vacation.adjustments)
    const totalAllowance = roundDays(allowance + carryover + adjustments)
    const used = roundDays(vacation.used)
    const remaining = roundDays(vacation.available)
    const usedPast = Math.max(0, roundDays(used - upcomingApproved))

    // Days taken so far this year is computed server-side (elapsed, holiday-aware
    // business days) and carried on the balance — date-accurate and identical to
    // the dashboard. The proration/formula is the shared computeAccruedBalance;
    // the accrual clock opens at the hire / policy effective date (TASK-348).
    const takenThisYear = roundDays(vacation.takenThisYear)
    const accrualStart = vacation.accrualStart ?? null
    const accrued = computeAccruedBalance(
        allowance,
        carryover,
        takenThisYear,
        today,
        accrualStart,
        adjustments,
    )

    return {
        emoji: getLeaveBadgeEmoji(vacation.badgeType),
        leaveTypeName: vacation.leaveTypeName,
        policyName: vacation.policyName,
        allowance,
        carryover,
        adjustments,
        totalAllowance,
        usedPast,
        upcomingApproved,
        remaining,
        pending: roundDays(vacation.pending),
        unplanned: roundDays(vacation.available),
        accruedToDate: accrued.accruedToDate,
        totalAccrued: accrued.totalAccrued,
        takenThisYear,
        balanceAsOfToday: accrued.balanceAsOfToday,
        accruedSince: accrualWindowStart(today, accrualStart),
    }
}

/** Facts for the read-only Details card, all derived from the assigned policies. */
export const buildDetailsData = (balances: ProfileLeaveBalance[]): TimeAwayDetails => {
    const vacation = findVacation(balances)
    return {
        policyName: vacation?.policyName ?? null,
        approvalMode: vacation ? (vacation.requiresApproval ? 'manager' : 'automatic') : null,
        toovabaAssigned: balances.some((b) => b.leaveTypeSlug === SLUG_TOOVABA),
    }
}

/** Soonest public holiday on or after today, or null when none remain this year. */
export const nextUpcomingHoliday = (
    holidays: { date: string; name: string }[],
    today: string,
): { date: string; name: string } | null => {
    const upcoming = holidays
        .filter((h) => h.date >= today)
        .sort((a, b) => a.date.localeCompare(b.date))
    return upcoming[0] ?? null
}
